/**
 * ViewModeSelector Component
 * 
 * A dropdown selector that allows users to switch between different
 * views of their Parse Server application (Preview, Database, Cloud Code, etc.)
 * 
 * Features:
 * - Clean glass-morphism design consistent with the app
 * - Custom dropdown with icons for each view mode
 * - Keyboard accessible
 */

import { useCallback, useRef, useState, useEffect } from 'react';
import { ViewMode, VIEW_MODE_OPTIONS } from '../../types/AppPanel';

// Import icons as React components
import { ReactComponent as PreviewIcon } from '../../assets/images/icon-preview.svg';
import { ReactComponent as DatabaseIcon } from '../../assets/images/icon-database.svg';
import { ReactComponent as CloudCodeIcon } from '../../assets/images/icon-cloud-code.svg';
import { ReactComponent as DeploymentsIcon } from '../../assets/images/menu-deployments.svg';
import { ReactComponent as LogsIcon } from '../../assets/images/menu-logs.svg';
import { ReactComponent as ChevronDownIcon } from '../../assets/images/icon-chevron-down.svg';

interface ViewModeSelectorProps {
  /** Currently selected view mode */
  value: ViewMode;
  /** Callback when view mode changes */
  onChange: (mode: ViewMode) => void;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

/**
 * Maps icon IDs to their corresponding React components
 */
const ICON_MAP: Record<string, React.FC<React.SVGProps<SVGSVGElement>>> = {
  'preview': PreviewIcon,
  'database': DatabaseIcon,
  'cloud-code': CloudCodeIcon,
  'deployments': DeploymentsIcon,
  'logs': LogsIcon,
};

/**
 * Renders the appropriate icon for a view mode
 */
const ViewModeIcon = ({ iconId, className }: { iconId: string; className?: string }) => {
  const IconComponent = ICON_MAP[iconId];
  if (!IconComponent) return null;
  return <IconComponent className={className} width={18} height={18} />;
};

export default function ViewModeSelector({ value, onChange, disabled }: ViewModeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Find the currently selected option
  const selectedOption = VIEW_MODE_OPTIONS.find(opt => opt.id === value) || VIEW_MODE_OPTIONS[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (disabled) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        setIsOpen(!isOpen);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        if (!isOpen) {
          event.preventDefault();
          setIsOpen(true);
        }
        break;
    }
  }, [isOpen, disabled]);

  // Handle option selection
  const handleSelect = useCallback((mode: ViewMode) => {
    onChange(mode);
    setIsOpen(false);
  }, [onChange]);

  return (
    <div 
      ref={containerRef}
      className="relative"
    >
      {/* Trigger Button - responsive: icon only on small screens */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          flex items-center gap-1.5 md:gap-2.5 px-2.5 md:px-4 py-2 rounded-lg
          bg-white/5 border border-white/10
          hover:bg-white/8 hover:border-white/15
          focus:outline-none focus:ring-2 focus:ring-[#208AEC]/40 focus:border-[#208AEC]/50
          transition-all duration-200 flex-shrink-0
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`View mode: ${selectedOption.label}`}
      >
        <ViewModeIcon iconId={selectedOption.iconId} className="text-[#208AEC]" />
        {/* Label hidden on small/medium screens */}
        <span className="hidden md:inline text-sm font-semibold text-white">{selectedOption.label}</span>
        <ChevronDownIcon 
          className={`text-white/60 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          width={14}
          height={14}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && !disabled && (
        <div 
          className="
            absolute top-full left-0 mt-1 z-[9999]
            min-w-[180px] py-1
            bg-[#0c121c] backdrop-blur-xl
            border border-white/10 rounded-lg
            shadow-xl shadow-black/50
          "
          role="listbox"
          aria-label="Select view mode"
        >
          {VIEW_MODE_OPTIONS.map((option) => {
            const isSelected = option.id === value;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => handleSelect(option.id)}
                className={`
                  w-full flex items-center gap-3 px-4 py-2.5
                  text-left text-sm
                  transition-colors duration-150
                  ${isSelected 
                    ? 'bg-[#208AEC]/15 text-white' 
                    : 'text-white/80 hover:bg-white/5 hover:text-white'
                  }
                `}
                role="option"
                aria-selected={isSelected}
              >
                <ViewModeIcon 
                  iconId={option.iconId} 
                  className={isSelected ? 'text-[#208AEC]' : 'text-white/50'}
                />
                <span className="font-medium">{option.label}</span>
                {isSelected && (
                  <svg 
                    className="ml-auto text-[#208AEC]" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 16 16" 
                    fill="none"
                  >
                    <path 
                      d="M3 8L6.5 11.5L13 5" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

